"use strict";
/**
 * Google Calendar Integration — Constants
 *
 * Implements the appointment standard documented in
 * `resources/notes/google-calendar/standard-v1.md`.
 *
 * Consumed by:
 * - Backend diagnostic engine (parser + scorer)
 * - Frontend dashboard (dimension labels, weights)
 *
 * Changes to scoring rules require a new version (STANDARD_V2) so previous
 * runs remain reproducible.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.NOTICE_REMINDER_TITLE_REGEX = exports.NOTICE_CELEBRATION_TITLE_REGEX = exports.NOTICE_BLOCK_TITLE_REGEX = exports.NOTICE_KIND_COLOR_IDS = exports.NOTICE_KIND_LABELS = exports.NOTICE_KINDS = exports.HVP_EVENT_PROP_KEYS = exports.KNOWN_BREEDS = exports.PET_AGE_REGEX = exports.LABELED_DESCRIPTION_FIELDS = exports.LABELED_DESCRIPTION_FORMAT_VERSION = exports.SCHEDULER_DATE_FALLBACK_REGEX = exports.SCHEDULER_WITH_DATE_REGEX = exports.SCHEDULER_REGEX = exports.PHONE_REGEX = exports.QVET_ID_REGEX = exports.CANCELLED_TITLE_REGEX = exports.REMINDER_TITLE_REGEX = exports.PREFERRED_VET_REGEX = exports.EXCLUDED_TITLE_PREFIXES = exports.SERVICE_CODE_SYNONYMS = exports.CANONICAL_SERVICE_CODES = exports.EXCLUDED_COLOR_IDS = exports.NO_SHOW_COLOR_ID = exports.CANCELLED_COLOR_ID = exports.COLOR_ID_TO_BRANCH = exports.BRANCH_COLOR_IDS = exports.STANDARD_V1 = exports.STANDARD_V3 = exports.STANDARD_V2 = void 0;
exports.detectDescriptionFormat = detectDescriptionFormat;
exports.extractLabeledField = extractLabeledField;
exports.extractTutorQvetIdLabeled = extractTutorQvetIdLabeled;
exports.extractMotivoLabeled = extractMotivoLabeled;
// ─── Standard v1 ─────────────────────────────────────────────────────────────
/**
 * Multi-factor appointment standard v2.
 *
 * Ten dimensions covering everything a recepción needs to capture to make an
 * appointment actionable: branch, title format, pet identity (name/age/breed),
 * tutor identity (name/phone), motive, and scheduling provenance (who and when).
 *
 * Weights sum to 100. An event is "compliant" when its score >= 80.
 */
exports.STANDARD_V2 = {
    version: "2.0",
    minimumCompliantScore: 80,
    dimensions: [
        {
            id: "branch",
            weight: 10,
            label: "Sucursal",
            description: 'Sucursal detectada por colorId del evento: 11 (Tomato/rojo) = Urban, 9 (Blueberry/azul) = Harbor, 3 (Grape/morado) = Montejo. Si el evento usa el color por defecto, falla esta dimensión. Atención: hay tonos parecidos en GCal — fíjate en el colorId exacto en el drawer si dudas.',
        },
        {
            id: "title",
            weight: 10,
            label: "Título bien formado",
            description: 'El título empieza con un código de servicio reconocible del diccionario: C (consulta), SC (sin cita), CS (consulta seguimiento), V/VAC (vacuna), CX (cirugía), CERT (certificado), USG, RX, ECO, OVH, TM (toma de muestras), AP/APL/AM (aplicación), D (desparasitación), etc.',
        },
        {
            id: "pet_name",
            weight: 10,
            label: "Nombre de la mascota",
            description: 'Nombre de la mascota detectable en el título (después del código de servicio y antes de las iniciales del vet). Ej: "C. MILO RGL*" → mascota = MILO.',
        },
        {
            id: "pet_age",
            weight: 8,
            label: "Edad de la mascota",
            description: 'Edad anotada en la descripción: "4 meses", "1 año y 4 meses", "8 semanas", "2 años". Importante para vacunación, dosis y diagnóstico — sin edad, no se puede protocolizar.',
        },
        {
            id: "pet_breed",
            weight: 8,
            label: "Raza de la mascota",
            description: 'Raza detectada en la descripción contra un diccionario de razas comunes: Yorkie, Shih Tzu, Cocker Spaniel, French Bulldog, Chihuahua, Schnauzer, Golden Retriever, Labrador, Persa, Siamés, mestizo, etc. Útil para protocolos clínicos y dosificación.',
        },
        {
            id: "owner_name",
            weight: 12,
            label: "Nombre del tutor",
            description: 'Nombre del cliente/tutor. Auto-pasa si hay Q{id} en la descripción (cliente linkeado a QVET) o si el teléfono matchea un cliente QVET existente. Si no, busca nombre completo del tutor en la descripción (mínimo 2 palabras, mayúsculas/minúsculas con letras).',
        },
        {
            id: "owner_phone",
            weight: 12,
            label: "Teléfono del tutor",
            description: 'Teléfono del tutor en cualquier formato común (con o sin +52, con o sin separadores). Auto-pasa si hay Q{id} (sabemos el teléfono del registro QVET). Necesario para confirmar la cita y avisar de cambios.',
        },
        {
            id: "context",
            weight: 10,
            label: "Motivo / contexto",
            description: 'La descripción tiene contexto clínico relevante (motivo de la visita, síntomas, antecedentes) más allá del nombre y teléfono. Mínimo 20 caracteres tras descontar nombre, teléfono y Q{id}.',
        },
        {
            id: "scheduler",
            weight: 10,
            label: "Iniciales de quien agendó",
            description: 'Iniciales (col_code) del colaborador que agendó la cita, dentro de la descripción. Formatos válidos: "AGENDO XZA", "AG YMP", "AGENDADO POR SLR". Permite reportar productividad por persona.',
        },
        {
            id: "scheduling_date",
            weight: 10,
            label: "Fecha en que se agendó",
            description: 'Fecha de la anotación de quién agendó, junto a sus iniciales. Ejemplos: "AGENDO XZA 16 MAYO", "AG YMP 14.05.26", "AGENDADO POR SLR 02/12/25". Permite medir el lead time entre cuándo se agendó y cuándo es la cita.',
        },
    ],
};
/**
 * Multi-factor appointment standard v3.
 *
 * Twelve dimensions. Compared to V2:
 * - `title` is split into 3 dimensions: `title_service_code`, `title_pet_name`,
 *   `title_format` — so each aspect of "well-formed title" gets its own weight.
 * - `discount_format` added: auto-passes when no discount mentioned, but if
 *   a `%` is present in the description, must follow `\d+% <razón>` format.
 * - Weights rebalanced: title + branch weighted higher (12 each); owner +
 *   context downgraded to 8; raza/edad downgraded to 4. Discount = 4.
 *
 * Weights sum to 100. An event is "compliant" when its score >= 80.
 */
exports.STANDARD_V3 = {
    version: "3.0",
    minimumCompliantScore: 80,
    dimensions: [
        {
            id: "branch",
            weight: 8,
            label: "Sucursal",
            description: 'Sucursal detectada por colorId del evento: 11 (Tomato/rojo) = Urban, 9 (Blueberry/azul) = Harbor, 3 (Grape/morado) = Montejo. Si el evento usa el color por defecto, falla esta dimensión. Atención: hay tonos parecidos en GCal — fíjate en el colorId exacto en el drawer si dudas.',
        },
        {
            id: "title_service_code",
            weight: 12,
            label: "Código de servicio",
            description: 'El título empieza con un código del diccionario: C (consulta), SC (sin cita), CS (consulta seguimiento), V/VAC (vacuna), CX (cirugía), CERT (certificado), USG, RX, ECO, OVH, TM (toma de muestras), AM/AP/APL (aplicación de medicamento), D (desparasitación), MC (microchip), CU (corte de uñas), PROFILAXIS, etc.',
        },
        {
            id: "title_pet_name",
            weight: 12,
            label: "Nombre de la mascota (título)",
            description: 'Nombre de la mascota detectable en el título (después del código de servicio, antes de iniciales del vet). Ej: "C MILO RGL*" → mascota = MILO. Soporta multi-mascota: "V CUBA, TEQUILA Y WHISKY".',
        },
        {
            id: "title_format",
            weight: 12,
            label: "Formato del título",
            description: 'Longitud ≤ 40 caracteres, sin info clínica verbosa, asterisco solo al final (médico preferido — nunca doble ni en medio), iniciales del vet al final si están presentes. "MANEJO INTRAHOSPITALARIO MINNIE" o "DUTTON FPO**" fallan.',
        },
        {
            id: "scheduling_date",
            weight: 8,
            label: "Fecha en que se agendó",
            description: 'Fecha de la anotación de quién agendó, junto a sus iniciales. Ejemplos: "AGENDO XZA 16 MAYO", "AG YMP 14.05.26", "AGENDADO POR SLR 02/12/25". Permite medir el lead time entre cuándo se agendó y cuándo es la cita.',
        },
        {
            id: "scheduler",
            weight: 12,
            label: "Iniciales de quien agendó",
            description: 'Iniciales (col_code) del colaborador que agendó la cita, dentro de la descripción. Formatos válidos: "AGENDO XZA", "AG YMP", "AGENDADO POR SLR". Crítico para rastrear responsable de la calidad de la agenda.',
        },
        {
            id: "context",
            weight: 8,
            label: "Motivo / contexto",
            description: 'Eventos en formato labeled: cuenta el bloque "Motivo:" (>20 caracteres). Eventos legacy posicionales: la descripción tiene contexto clínico relevante más allá del nombre y teléfono — mínimo 20 caracteres tras descontar nombre, teléfono y Q{id}.',
        },
        {
            id: "owner_name",
            weight: 8,
            label: "Nombre del tutor",
            description: 'Nombre del cliente/tutor. Auto-pasa si hay Q{id} en la descripción (cliente linkeado a QVET) o si el teléfono matchea un cliente QVET existente. Eventos labeled: lo lee de la línea "Tutor:". Eventos legacy: lo busca en la primera línea con 2+ palabras de letras (o el patrón "NAME | PETS | Q{id}").',
        },
        {
            id: "owner_phone",
            weight: 8,
            label: "Teléfono del tutor",
            description: 'Teléfono del tutor en cualquier formato común (con o sin +52, con o sin separadores). Eventos labeled: lo lee de la línea "Teléfonos:". Eventos legacy: lo busca en cualquier línea de la descripción. Auto-pasa si hay Q{id} (sabemos el teléfono del registro QVET).',
        },
        {
            id: "pet_age",
            weight: 4,
            label: "Edad de la mascota",
            description: 'Edad anotada en la descripción. Eventos labeled: línea "Edad:". Eventos legacy: detección por patrón ("4 meses", "1 año y 4 meses", "8 semanas", "2 años"). Importante para vacunación, dosis y diagnóstico.',
        },
        {
            id: "pet_breed",
            weight: 4,
            label: "Raza de la mascota",
            description: 'Raza detectada en la descripción. Eventos labeled: línea "Raza:". Eventos legacy: match contra un diccionario de razas comunes (Yorkie, Shih Tzu, Cocker Spaniel, French Bulldog, Chihuahua, Schnauzer, Golden Retriever, Labrador, Persa, Siamés, mestizo, etc.).',
        },
        {
            id: "discount_format",
            weight: 4,
            label: "Formato de descuento",
            description: 'Si la descripción menciona un descuento (contiene un patrón \\d+%), debe seguir el formato "<porcentaje>% <razón>" — ejemplos: "15% recordatorio", "10% nuevo cliente", "30% primera vez", "50% cortesía". Si no hay descuento mencionado, esta dimensión pasa automáticamente.',
        },
    ],
};
/**
 * Backward-compatible alias for existing code/tests that imported `STANDARD_V1`.
 * Always points to the current standard (V3 as of 13.5.0).
 *
 * @deprecated Use `STANDARD_V3` (or whatever the current version is) explicitly.
 */
exports.STANDARD_V1 = exports.STANDARD_V3;
// ─── ColorId → Branch mapping ────────────────────────────────────────────────
/**
 * Google Calendar colorId values mapped to HVP branches.
 *
 * Confirmed via Phase 0 discovery (2026-05-18):
 * - 11 (Tomato) → Urban
 * - 9  (Blueberry) → Harbor
 * - 3  (Grape) → Montejo (citas) + recordatorios operacionales
 */
exports.BRANCH_COLOR_IDS = {
    urban: "11",
    harbor: "9",
    montejo: "3",
};
/**
 * Reverse lookup: colorId → branch.
 */
exports.COLOR_ID_TO_BRANCH = {
    "11": "urban",
    "9": "harbor",
    "3": "montejo",
};
// ─── Excluded colorIds ───────────────────────────────────────────────────────
/** colorId 8 (Graphite) = cancelled. Tracked as a separate metric. */
exports.CANCELLED_COLOR_ID = "8";
/**
 * colorId 7 (Peacock cyan) = cliente no llegó (no-show).
 *
 * Convention: when a client doesn't show up to their appointment, recepción
 * repinta el evento con este color para señalar pendiente-de-seguimiento.
 * Tracked separately from cancellations (which are client-initiated) and
 * excluded from compliance scoring.
 */
exports.NO_SHOW_COLOR_ID = "7";
/**
 * colorIds reserved for special events (NOT patient appointments).
 *
 * - 2 (Sage) — aniversario HVP, "no agendar nada", recordatorios admin
 * - 4 (Flamingo) — cumpleaños colaboradores
 * - 5 (Banana) — bloqueos (vacaciones, cursos, NO AGENDAR)
 * - 10 (Basil) — reuniones, eventos importantes
 */
exports.EXCLUDED_COLOR_IDS = new Set([
    "2",
    "4",
    "5",
    "10",
]);
// ─── Service codes ───────────────────────────────────────────────────────────
/**
 * Canonical service codes — the diagnostic engine's source of truth.
 *
 * The parser maps free-form prefixes (via SERVICE_CODE_SYNONYMS) to one of these.
 */
exports.CANONICAL_SERVICE_CODES = [
    "CONSULTA",
    "SIN_CITA",
    "CONSULTA_SEGUIMIENTO",
    "VACUNA",
    "CIRUGIA",
    "CERTIFICADO",
    "ULTRASONIDO",
    "RADIOGRAFIA",
    "ECOCARDIO",
    "OVH",
    "PROFILAXIS_DENTAL",
    "ANESTESIA",
    "EUTANASIA",
    "RETIRO_PUNTOS",
    "REVISION",
    "DESPARASITACION",
    "APLICACION",
    "TOMA_MUESTRAS",
    "TERAPIA_MEDICA",
    "INYECCION",
];
/**
 * Maps free-form prefixes (uppercase, trimmed, period-stripped) to canonical codes.
 *
 * Example: title "C. OFT LUKE RGL*" → first token "C" → CONSULTA.
 */
exports.SERVICE_CODE_SYNONYMS = {
    // Consulta
    C: "CONSULTA",
    CONSULTA: "CONSULTA",
    // Sin Cita (walk-in)
    SC: "SIN_CITA",
    // Consulta Seguimiento
    CS: "CONSULTA_SEGUIMIENTO",
    // Vacuna
    V: "VACUNA",
    VAC: "VACUNA",
    VACUNA: "VACUNA",
    // Cirugía
    CX: "CIRUGIA",
    CIRUGIA: "CIRUGIA",
    CIRUGÍA: "CIRUGIA",
    // Certificado
    CERT: "CERTIFICADO",
    CERTIF: "CERTIFICADO",
    CERTIFICADO: "CERTIFICADO",
    // Ultrasonido
    USG: "ULTRASONIDO",
    ULTRASONIDO: "ULTRASONIDO",
    // Radiografía
    RX: "RADIOGRAFIA",
    RADIOGRAFIA: "RADIOGRAFIA",
    RADIOGRAFÍA: "RADIOGRAFIA",
    // Ecocardio
    ECO: "ECOCARDIO",
    ECOCARDIOGRAMA: "ECOCARDIO",
    // OVH (ovariohisterectomía)
    OVH: "OVH",
    // Profilaxis dental
    PROFILAXIS: "PROFILAXIS_DENTAL",
    LIMPIEZA: "PROFILAXIS_DENTAL",
    // Anestesia
    ANESTESIA: "ANESTESIA",
    // Eutanasia
    EUTANASIA: "EUTANASIA",
    // Retiro de puntos / vendaje
    RETIRO: "RETIRO_PUNTOS",
    RET: "RETIRO_PUNTOS",
    // Revisión
    REV: "REVISION",
    REVISION: "REVISION",
    REVISIÓN: "REVISION",
    // Desparasitación
    D: "DESPARASITACION",
    DES: "DESPARASITACION",
    DESPARASITACION: "DESPARASITACION",
    DESPARASITACIÓN: "DESPARASITACION",
    // Aplicación de medicamento / microchip / etc.
    AP: "APLICACION",
    APL: "APLICACION",
    AM: "APLICACION",
    APLI: "APLICACION",
    APLICACION: "APLICACION",
    APLICACIÓN: "APLICACION",
    // Toma de muestras
    TM: "TOMA_MUESTRAS",
    // Terapia médica (visto como "T.M." → TM)
    T: "TERAPIA_MEDICA",
    // Inyección
    INYECCION: "INYECCION",
    INYECCIÓN: "INYECCION",
};
/**
 * Title prefixes (uppercase, first token) that mark NON-clinical events.
 *
 * Diagnostic engine excludes these — they are not patient appointments.
 */
exports.EXCLUDED_TITLE_PREFIXES = new Set([
    "RECIBIR",
    "ENTREGAR",
    "DESPACHAR",
    "DESP",
    "ENVIAR",
    "RECEPCION",
    "RECEPCIÓN",
    "RECORDATORIO",
    "RECORDATORIOS",
    "ENTREVISTA",
    "REUNION",
    "REUNIÓN",
    "CUMPLE",
    "CUMPLEAÑOS",
    "ANIVERSARIO",
    "VACACIONES",
    "NO",
    "IMPORTANTE",
]);
// ─── Title patterns ──────────────────────────────────────────────────────────
/**
 * Detects an asterisk at the end of the title (after optional whitespace).
 * `*` at the end = "médico preferido" = the vet earns a commission.
 *
 * Examples that match:
 *   "C. ARCHIE AAT*"
 *   "C. ARCHIE AAT *"
 *   "AM CAMILA APL*"
 */
exports.PREFERRED_VET_REGEX = /\*\s*$/;
/**
 * Detects "ENVIAR RECORDATORIOS *" titles (Montejo recordatorios). Even though
 * they live in colorId 3 (Montejo), they are NOT patient appointments.
 */
exports.REMINDER_TITLE_REGEX = /^ENVIAR\s+RECORDATORIOS\b/i;
/**
 * Detects cancelled events by title content. Used in addition to colorId 8.
 */
exports.CANCELLED_TITLE_REGEX = /\b(?:CANCEL(?:ADA|ADO))\b|\bse\s+cancela\b/i;
// ─── Description patterns ────────────────────────────────────────────────────
/**
 * Matches a QVET client id ("Q123456").
 * Range chosen wide enough to cover historical and future ids.
 */
exports.QVET_ID_REGEX = /Q\d{4,7}/;
/**
 * Captures Mexican phone numbers in any of the common written formats.
 * Returns the full match; caller normalizes to last 10 digits.
 *
 * Tolerates:
 *   +52 1 999 442 9488
 *   +52 999 442 9488
 *   999 442 9488
 *   999-442-9488
 *   9994429488
 */
exports.PHONE_REGEX = /(?:\+?52\s?1?\s?)?(?:\d{3}[\s-]?\d{3}[\s-]?\d{4}|\d{10})/;
/**
 * Captures the "agendado por" col_code from a description.
 *
 * Variants observed:
 *   "AGENDO XZA 16 MAYO"
 *   "AG YMP 14.05.26"
 *   "AGENDADA POR SLR 02/12/25"
 *   "AGENDADO POR SLR 02/12/25"
 */
exports.SCHEDULER_REGEX = /\b(?:AG[.:]?|AGE[.:]|AGEND[OÓ][.:]?|AGENG[OÓ]|AGED[OÓ]|AGENDAD[OA](?:\s+POR)?)\s+([A-Z]{2,4})\b/i;
/**
 * Captures the "agendado por" col_code AND the date annotation that often follows it.
 *
 *   "AGENDO XZA 16 MAYO"            → groups: XZA, "16 MAYO"
 *   "AG YMP 14.05.26"               → groups: YMP, "14.05.26"
 *   "AGENDADA POR SLR 02/12/25"     → groups: SLR, "02/12/25"
 *   "AGENDADO POR SLR"              → groups: SLR, undefined
 */
exports.SCHEDULER_WITH_DATE_REGEX = /\b(?:AG[.:]?|AGE[.:]|AGEND[OÓ][.:]?|AGENG[OÓ]|AGED[OÓ]|AGENDAD[OA](?:\s+POR)?)\s+([A-Z]{2,4})(?:\s+(?:el\s+|del\s+)?(\d{1,2}[./\-]\d{1,2}(?:[./\-]\d{2,4})?|\d{1,2}[./\-](?:ENE|FEB|MAR|ABR|MAY|JUN|JUL|AGO|SEP|OCT|NOV|DIC|ENERO|FEBRERO|MARZO|ABRIL|MAYO|JUNIO|JULIO|AGOSTO|SEPTIEMBRE|OCTUBRE|NOVIEMBRE|DICIEMBRE)(?:[./\-]\d{2,4})?|\d{1,2}\s+(?:ENE|FEB|MAR|ABR|MAY|JUN|JUL|AGO|SEP|OCT|NOV|DIC|ENERO|FEBRERO|MARZO|ABRIL|MAYO|JUNIO|JULIO|AGOSTO|SEPTIEMBRE|OCTUBRE|NOVIEMBRE|DICIEMBRE)))?\b/i;
/**
 * Fallback pattern for when recepción writes just a date followed by a
 * col_code, no `AGENDO/AG` prefix. Examples observed in production:
 *
 *   "20.11.2025 MGC"
 *   "18.11.25 MGC"
 *   "04/05/2026 FJV"
 *
 * Group 1: date string. Group 2: col_code candidate (must be validated
 * against `knownColCodes` by the caller).
 *
 * Used as a fallback when `SCHEDULER_REGEX` finds no AGENDO-prefixed
 * annotation. Prevents false positives by requiring an immediately-adjacent
 * date.
 */
exports.SCHEDULER_DATE_FALLBACK_REGEX = /\b(\d{1,2}[./\-]\d{1,2}(?:[./\-]\d{2,4})?|\d{1,2}[./\-](?:ENE|FEB|MAR|ABR|MAY|JUN|JUL|AGO|SEP|OCT|NOV|DIC|ENERO|FEBRERO|MARZO|ABRIL|MAYO|JUNIO|JULIO|AGOSTO|SEPTIEMBRE|OCTUBRE|NOVIEMBRE|DICIEMBRE)(?:[./\-]\d{2,4})?)\s+([A-Z]{2,4})\b/i;
// ─── Labeled description format (06/2026) ───────────────────────────────────
/**
 * Version tag stored in `extendedProperties.private.descriptionFormat` on
 * events created with the labeled format. Lets analytics / future migration
 * scripts distinguish labeled from positional events.
 */
exports.LABELED_DESCRIPTION_FORMAT_VERSION = "labeled-v1";
/**
 * Per-field spec for the labeled description format.
 *
 * Each field has:
 * - `label`: canonical label written when building (e.g., "Tutor").
 * - `regex`: extraction regex with capturing groups. Group 1 = value.
 *   For `tutor`, group 2 captures the optional QVET id digits.
 *
 * Order-independent: labels anchor each field, so the format tolerates
 * reordering or manual edits as long as the labels are present.
 *
 * Tolerates: `Tutor:`, `tutor :`, `TUTOR: `, trailing whitespace.
 */
exports.LABELED_DESCRIPTION_FIELDS = {
    tutor: {
        label: "Tutor",
        regex: /^\s*tutor\s*:\s*(.+?)(?:\s*\(\s*Q(\d{4,7})\s*\))?\s*$/im,
    },
    phones: {
        label: "Teléfonos",
        regex: /^\s*tel[eé]fonos?\s*:\s*(.+?)\s*$/im,
    },
    pet: {
        label: "Mascota",
        regex: /^\s*mascota\s*:\s*(.+?)\s*$/im,
    },
    breed: {
        label: "Raza",
        regex: /^\s*raza\s*:\s*(.+?)\s*$/im,
    },
    age: {
        label: "Edad",
        regex: /^\s*edad\s*:\s*(.+?)\s*$/im,
    },
    motivo: {
        label: "Motivo",
        // Motivo is multi-line; extraction handled by extractMotivoLabeled().
        // Regex below only detects presence of the label.
        regex: /^\s*motivo\s*:/im,
    },
    discount: {
        label: "Descuento",
        regex: /^\s*descuento\s*:\s*(.+?)\s*$/im,
    },
};
/**
 * Matches a line that starts a new labeled field or the AGENDÓ/REAGENDÓ block
 * or the HVP marker. Used to bound multi-line `Motivo:` capture.
 */
const LABELED_BOUNDARY_REGEX = /^\s*(?:tutor|tel[eé]fonos?|mascota|raza|edad|descuento|motivo|agend|reagend|—)/i;
/**
 * Returns "labeled" if any known field label is present at the start of a line
 * in the description; otherwise "positional" (legacy format).
 */
function detectDescriptionFormat(description) {
    if (!description)
        return "positional";
    for (const field of Object.values(exports.LABELED_DESCRIPTION_FIELDS)) {
        if (field.regex.test(description))
            return "labeled";
    }
    return "positional";
}
/**
 * Extract a single-line labeled field. Returns trimmed value or null.
 *
 * For `motivo` (which may span multiple lines), use {@link extractMotivoLabeled}.
 * For the tutor's QVET id, use {@link extractTutorQvetIdLabeled}.
 */
function extractLabeledField(description, field) {
    if (!description)
        return null;
    const match = description.match(exports.LABELED_DESCRIPTION_FIELDS[field].regex);
    const value = match?.[1]?.trim();
    return value && value.length > 0 ? value : null;
}
/**
 * Extract the QVET client id from the `Tutor: NAME (Q12345)` line. Returns the
 * numeric portion as a string (e.g., "12345") or null.
 */
function extractTutorQvetIdLabeled(description) {
    if (!description)
        return null;
    const match = description.match(exports.LABELED_DESCRIPTION_FIELDS.tutor.regex);
    const id = match?.[2]?.trim();
    return id && id.length > 0 ? id : null;
}
/**
 * Extract the motivo block which may span multiple lines. Starts at the
 * `Motivo:` line and collects subsequent lines until another labeled field,
 * an AGENDÓ/REAGENDÓ line, the HVP marker, or end of description.
 */
function extractMotivoLabeled(description) {
    if (!description)
        return null;
    const lines = description.split("\n");
    const startIdx = lines.findIndex((l) => /^\s*motivo\s*:/i.test(l));
    if (startIdx === -1)
        return null;
    const firstLine = lines[startIdx].replace(/^\s*motivo\s*:\s*/i, "");
    const collected = [firstLine];
    for (let i = startIdx + 1; i < lines.length; i++) {
        if (LABELED_BOUNDARY_REGEX.test(lines[i]))
            break;
        collected.push(lines[i]);
    }
    const value = collected.join("\n").trim();
    return value.length > 0 ? value : null;
}
// ─── Pet age detection ───────────────────────────────────────────────────────
/**
 * Captures pet age expressions in Spanish from descriptions.
 *
 *   "YORKIE 4 MESES"             → "4 MESES"
 *   "11 MESES, COCKER SPANIEL"    → "11 MESES"
 *   "1 año y 4 meses"            → "1 año y 4 meses"
 *   "8 semanas"                  → "8 semanas"
 *   "2 AÑOS"                     → "2 AÑOS"
 */
exports.PET_AGE_REGEX = /\b\d+\s*(?:años?|añoss?|meses?|semanas?|días?|años?\s+y\s+\d+\s+meses?)\b/i;
// ─── Pet breed detection ────────────────────────────────────────────────────
/**
 * Common dog/cat breeds observed in HVP descriptions. Used by the parser to
 * detect a breed mention. Match is case-insensitive whole-word.
 *
 * NOT exhaustive — extend as new breeds appear in real data.
 */
exports.KNOWN_BREEDS = [
    // Perros
    "yorkie",
    "yorkshire",
    "shih tzu",
    "shihtzu",
    "schnauzer",
    "schnauzer mini",
    "cocker spaniel",
    "cocker",
    "golden retriever",
    "golden",
    "labrador",
    "labrador retriever",
    "chihuahua",
    "bulldog",
    "bulldog frances",
    "bulldog francés",
    "bulldog ingles",
    "bulldog inglés",
    "french bulldog",
    "poodle",
    "french poodle",
    "caniche",
    "bichon",
    "bichón",
    "bichon maltes",
    "maltes",
    "maltés",
    "border collie",
    "border",
    "pug",
    "carlino",
    "pastor aleman",
    "pastor alemán",
    "pastor",
    "husky",
    "husky siberiano",
    "boxer",
    "boxer aleman",
    "pitbull",
    "pit bull",
    "american bully",
    "doberman",
    "rottweiler",
    "dachshund",
    "salchicha",
    "beagle",
    "dalmata",
    "dalmata",
    "san bernardo",
    "akita",
    "shar pei",
    "sharpei",
    "mestizo",
    "criollo",
    "xolo",
    "xoloitzcuintle",
    "chow chow",
    // Gatos
    "persa",
    "siames",
    "siamés",
    "british shorthair",
    "maine coon",
    "ragdoll",
    "bengala",
    "esfinge",
    "sphynx",
    "mestizo",
    "domestic shorthair",
    "domestic short hair",
    "domestico",
    "doméstico",
];
// ─── HVP-created extendedProperties keys ─────────────────────────────────────
/**
 * Keys used in Google Calendar `extendedProperties.private` for events created
 * from HVP. Phase 2 writes these; Phase 1 reads them for full attribution.
 */
exports.HVP_EVENT_PROP_KEYS = {
    qvetClientId: "qvetClientId",
    qvetPetId: "qvetPetId",
    serviceCode: "serviceCode",
    branchId: "branchId",
    vetColCode: "vetColCode",
    scheduledByColCode: "scheduledByColCode",
    isPreferredVet: "isPreferredVet",
    source: "source", // "hvp"
    standardVersion: "standardVersion",
    /**
     * Marks the description-builder format version used at create/update time
     * (e.g., "labeled-v1"). Informational only — the parser detects format from
     * content, not from this prop. Useful for analytics ("% of events on labeled
     * format") and future migration scripts.
     */
    descriptionFormat: "descriptionFormat",
    /** Set on notices created from HVP (`NoticeKind`). Marks the event as not an appointment. */
    noticeKind: "noticeKind",
};
// ─── Notices (non-appointment events) ────────────────────────────────────────
exports.NOTICE_KINDS = [
    "block",
    "celebration",
    "operation",
    "reminder",
    "other",
];
exports.NOTICE_KIND_LABELS = {
    block: "Bloqueo · no agendar",
    celebration: "Celebración",
    operation: "Operación",
    reminder: "Recordatorio",
    other: "Otro",
};
/**
 * colorId given to notices created from HVP — the same colors staff already
 * use by hand: 5 Banana for blocks and reminders, 4 Flamingo for birthdays,
 * 10 Basil for inventory / recetas windows, 2 Sage for anything else.
 */
exports.NOTICE_KIND_COLOR_IDS = {
    block: "5",
    celebration: "4",
    operation: "10",
    reminder: "5",
    other: "2",
};
/** Titles that mark a block even without color: «NO AGENDAR …», «CERRADO …». */
exports.NOTICE_BLOCK_TITLE_REGEX = /\bNO\s+AGENDAR\b|^\s*CERRAD[OA]\b/i;
/** Titles of birthdays and anniversaries. */
exports.NOTICE_CELEBRATION_TITLE_REGEX = /\b(?:CUMPLE(?:A[ÑN]OS)?|ANIVERSARIO)\b/i;
/** Staff to-dos: «ENVIAR RECORDATORIOS», «RECORDAR CITA …», «RECIBIR A MILO», «ENTREGAR LUNA». */
exports.NOTICE_REMINDER_TITLE_REGEX = /^\s*(?:ENVIAR|RECORDAR|RCORDAR|RECIBIR|ENTREGAR|DESPACHAR|DESP|RECEPCI[OÓ]N)\b/i;
